/**
 * Post-assignment lifecycle actions for enterprise subscriptions.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Typography,
} from '@mui/material';
import { AppButton, AppTextField } from '../design-system';
import { providerEnterpriseService } from '../../services/ProviderEnterpriseService';
import * as dt from '../../../../shared/src/data.types';

export type EnterpriseLifecycleMode = 'extend-pilot' | 'record-payment' | 'expire-pilot';

interface EnterpriseLifecycleDialogProps {
  open: boolean;
  mode: EnterpriseLifecycleMode;
  tenantId: string;
  tenantName: string;
  onClose: () => void;
  onSuccess: () => void;
}

const MODE_TITLES: Record<EnterpriseLifecycleMode, string> = {
  'extend-pilot': 'Extend pilot',
  'record-payment': 'Record payment received',
  'expire-pilot': 'Expire pilot',
};

const EnterpriseLifecycleDialog: React.FC<EnterpriseLifecycleDialogProps> = ({
  open,
  mode,
  tenantId,
  tenantName,
  onClose,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [additionalDays, setAdditionalDays] = useState(30);
  const [providerNotes, setProviderNotes] = useState('');
  const [paymentReference, setPaymentReference] = useState('');
  const [expireReason, setExpireReason] = useState('');

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError(null);
      const typedTenantId = tenantId as dt.Uuid;

      switch (mode) {
        case 'extend-pilot':
          await providerEnterpriseService.extendEnterprisePilot({
            tenantId: typedTenantId,
            additionalDays,
            providerNotes: providerNotes || undefined,
          });
          break;
        case 'record-payment':
          await providerEnterpriseService.recordEnterprisePayment({
            tenantId: typedTenantId,
            paymentReference: paymentReference || undefined,
          });
          break;
        case 'expire-pilot':
          await providerEnterpriseService.expireEnterprisePilot({
            tenantId: typedTenantId,
            reason: expireReason || undefined,
          });
          break;
        default:
          break;
      }

      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{MODE_TITLES[mode]}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          <Typography variant="body2" color="text.secondary">
            Tenant: <strong>{tenantName}</strong>
          </Typography>

          {mode === 'extend-pilot' && (
            <>
              <AppTextField
                label="Additional days"
                type="number"
                value={additionalDays}
                onChange={(e) => setAdditionalDays(Number(e.target.value))}
                fullWidth
              />
              <AppTextField
                label="Notes"
                value={providerNotes}
                onChange={(e) => setProviderNotes(e.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </>
          )}

          {mode === 'record-payment' && (
            <>
              <Typography variant="body2" color="text.secondary">
                Record the invoice payment in Zoho first. This action only renews Insight access
                (extends the contract period and clears past-due grace).
              </Typography>
              <AppTextField
                label="Payment reference"
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                placeholder="Zoho invoice #, Mercury wire ref, etc."
                fullWidth
              />
            </>
          )}

          {mode === 'expire-pilot' && (
            <AppTextField
              label="Reason"
              value={expireReason}
              onChange={(e) => setExpireReason(e.target.value)}
              multiline
              minRows={2}
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <AppButton onClick={onClose} disabled={submitting}>
          Cancel
        </AppButton>
        <AppButton variant="contained" onClick={() => void handleSubmit()} disabled={submitting}>
          {submitting ? 'Saving…' : 'Confirm'}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default EnterpriseLifecycleDialog;
